// src/hooks/useSmoothScrollToBottom.ts
import { useRef, useEffect, useCallback } from 'react';

function smoothScrollToBottom(
    element: HTMLElement,
    duration = 500,
    cancelRef: React.MutableRefObject<(() => void) | null>
) {
    const start = element.scrollTop;
    const end = element.scrollHeight - element.clientHeight;
    const distance = end - start;

    if (distance <= 0) return;

    const minDuration = 400;
    const maxDuration = 2000;
    const pxPerMs = 2;
    const dynamicDuration = Math.min(
        maxDuration,
        Math.max(minDuration, Math.abs(distance) / pxPerMs)
    );

    const startTime = performance.now();
    let cancelled = false;
    let frameId: number;

    const cancelScroll = () => {
        cancelled = true;
        if (frameId) {
            cancelAnimationFrame(frameId);
        }
        element.removeEventListener('wheel', cancelScroll);
        element.removeEventListener('touchstart', cancelScroll);
        element.removeEventListener('mousedown', cancelScroll);
    };

    // Store cancel function for cleanup
    cancelRef.current = cancelScroll;

    const step = (currentTime: number) => {
        if (cancelled) return;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / dynamicDuration, 1);
        const ease = 1 - Math.pow(1 - progress, 1);

        element.scrollTop = start + distance * ease;

        if (progress < 1) {
            frameId = requestAnimationFrame(step);
        } else {
            cancelScroll();
        }
    };

    element.addEventListener('wheel', cancelScroll, { passive: true });
    element.addEventListener('touchstart', cancelScroll, { passive: true });
    element.addEventListener('mousedown', cancelScroll, { passive: true });

    frameId = requestAnimationFrame(step);
}

export function useSmoothScrollToBottom(
    ref: React.RefObject<HTMLElement>,
    trigger: any,
    duration = 400
) {
    const cancelRef = useRef<(() => void) | null>(null);

    const scrollToBottom = useCallback(() => {
        if (ref.current) {
            // Cancel any previous scroll
            if (cancelRef.current) cancelRef.current();
            smoothScrollToBottom(ref.current, duration, cancelRef);
        }
    }, [ref, duration]);

    useEffect(() => {
        scrollToBottom();
        // Cleanup on unmount or trigger change
        const cancel = cancelRef.current;
        return () => {
            if (cancel) cancel();
        };
    }, [trigger, scrollToBottom, ref]);

    return scrollToBottom;
}
